import { useEffect, useState } from 'react'
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core'
import {
  IconAlertTriangle,
  IconExternalLink,
  IconInfoCircle,
  IconLink,
  IconLock,
  IconPlayerPlay,
  IconRefresh,
} from '@tabler/icons-react'
import { initialParameterValues } from '../../services/etlJobsApi'
import { useTriggerEtlJob } from '../../hooks/useEtlJobs'
import { hangfireJobUrl } from '../../utils/hangfireUrl'
import {
  concurrencyNote,
  daysText,
  linkedAlertText,
  requestStatusDetailText,
  resumeNote,
  windowReadout,
} from './requestFormat'
import type { IgnoredLinkValue, WindowReadout } from './requestFormat'
import type { ResumeRule } from '../../types/etlProgress'
import type { EtlJob, EtlJobParameter } from '../../types/etlJobs'

interface RunJobModalProps {
  job: EtlJob | null
  /** The Hangfire dashboard's home page, or null when this build was not told where it is: render text. */
  hangfireDashboardUrl: string | null
  onClose: () => void
  /**
   * Starting values in place of the declared defaults — a deep link's, already reduced to the parameters the
   * job declares (linkProvenance). Absent for a job opened from its Run button.
   */
  initialValues?: Record<string, string> | null
  /** Parameters whose starting value came from the link and differs from its default. */
  linkedNames?: readonly string[]
  /**
   * A deep link chose the job. Said even when no value differs from its default: the job itself is somebody
   * else's choice, and a form opened this way must not look like one opened from the job's Run button.
   */
  openedFromLink?: boolean
  /** Link values that named a declared parameter but were not taken, and why. */
  ignoredLinkValues?: readonly IgnoredLinkValue[]
  /** The catalogue's resume rule: what a Warehouse or Entity request will actually do. */
  resume?: ResumeRule | null
  /**
   * Why the server would refuse this run right now — the caller's permission or an ETL halt — or null. The
   * form still opens (a deep link lands here without passing the table's Run button), but cannot submit.
   */
  blockedReason?: string | null
  /** Whether this build can sign anyone in with Entra; words a sign-in refusal (never "Sign in again" without it). */
  entraConfigured?: boolean
}

const NO_LINKED_NAMES: readonly string[] = []
const NO_IGNORED: readonly IgnoredLinkValue[] = []

/**
 * The run form, rendered from whatever the job declared.
 *
 * Nothing here knows about modules, entities or date windows specifically — the server publishes the
 * parameter list and this renders it. A job that gains an argument gains a field with no change to this
 * file, which is the whole reason the catalogue carries a schema rather than the UI hardcoding one.
 *
 * A DEEP LINK IS SOMEBODY ELSE'S CHOICE ARRIVING IN YOUR FORM. So every value it set that differs from the
 * default is marked, with the default beside it; the window's length is always stated, and called out when
 * it is longer than the job's default (a link can stage years over tables that have never been fully
 * loaded); and nothing is ever submitted but by the Run now button.
 */
export function RunJobModal({
  job,
  hangfireDashboardUrl,
  onClose,
  initialValues = null,
  linkedNames = NO_LINKED_NAMES,
  openedFromLink = false,
  ignoredLinkValues = NO_IGNORED,
  resume = null,
  blockedReason = null,
  entraConfigured = true,
}: RunJobModalProps) {
  const [values, setValues] = useState<Record<string, string>>({})
  const { trigger, isPending, request, followTimedOut, error, reset } = useTriggerEtlJob(entraConfigured)

  const jobId = job?.jobId ?? null

  useEffect(() => {
    if (job) {
      setValues(initialValues ?? initialParameterValues(job.parameters))
      reset()
    }
    // Keyed on the job's id, not its object: a deep-linked job is re-derived from the catalogue on every
    // refetch (window focus included), and re-running on identity would wipe what the operator typed.
    // `reset` is stable, and initialValues belongs to the job it arrived with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId])

  if (!job) return null

  const set = (name: string, value: string) => setValues((v) => ({ ...v, [name]: value }))

  const defaults = initialParameterValues(job.parameters)

  // A window that still holds its defaults is a one-month incremental load. That is rarely what someone
  // opening this dialog to "reload a table" wants, and the difference is invisible unless it is said.
  const hasDateWindow = job.parameters.some((p) => p.kind === 'Date')
  const windowInfo = windowReadout(job.parameters, values, defaults)

  const resumeText = resumeNote(job.category, resume)
  const oneAtATime = concurrencyNote(job.category)

  const linkedNotice = linkedAlertText(linkedNames.length, openedFromLink)
  const jobLink = request?.hangfireJobId ? hangfireJobUrl(request.hangfireJobId) : null

  return (
    <Modal opened={!!job} onClose={onClose} title={`Run ${job.displayName}`} size="lg">
      <Stack gap="md">
        {linkedNotice && (
          <Alert icon={<IconLink size={16} />} color="orange" variant="light" title="Opened from a link">
            <Text size="sm">{linkedNotice}</Text>
          </Alert>
        )}

        {ignoredLinkValues.length > 0 && (
          <Alert color="gray" variant="light" title="Not taken from the link">
            <Stack gap={2}>
              {ignoredLinkValues.map((ignored) => (
                <Text key={ignored.name} size="sm">
                  {ignored.name}={ignored.value} was not used: {ignored.reason}. The field shows its default.
                </Text>
              ))}
            </Stack>
          </Alert>
        )}

        {blockedReason && (
          <Alert icon={<IconLock size={16} />} color="gray" variant="light" title="This run would be refused">
            <Text size="sm">{blockedReason}</Text>
          </Alert>
        )}

        {job.description && (
          <Text size="sm" c="dimmed">
            {job.description}
          </Text>
        )}

        {job.parameters.length === 0 && (
          <Text size="sm" c="dimmed">
            This job takes no parameters.
          </Text>
        )}

        {job.parameters.map((parameter) => {
          const value = values[parameter.name] ?? ''
          const fromLink =
            linkedNames.includes(parameter.name) && initialValues != null && value === initialValues[parameter.name]
          return (
            <Stack key={parameter.name} gap={4}>
              <ParameterField parameter={parameter} value={value} onChange={(v) => set(parameter.name, v)} />
              {fromLink && <FromLinkNote defaultValue={defaults[parameter.name] ?? ''} />}
            </Stack>
          )
        })}

        {windowInfo && <WindowLength readout={windowInfo} />}

        {hasDateWindow && (
          <Alert icon={<IconAlertTriangle size={16} />} color="yellow" variant="light">
            The window defaults to the last month. To load a specific period — or everything — set the
            start date back explicitly.
          </Alert>
        )}

        {resumeText.variant === 'reopen' && (
          <Alert icon={<IconRefresh size={16} />} color="yellow" variant="light" title="This will resume an earlier run">
            <Text size="sm">{resumeText.text}</Text>
          </Alert>
        )}
        {resumeText.variant === 'live' && (
          <Alert icon={<IconRefresh size={16} />} color="yellow" variant="light" title="A run is in progress">
            <Text size="sm">{resumeText.text}</Text>
          </Alert>
        )}
        {resumeText.variant === 'no-target' && (
          <Text size="xs" c="dimmed">
            {resumeText.text}
          </Text>
        )}

        {oneAtATime && (
          <Group gap={6} wrap="nowrap" align="flex-start">
            <IconInfoCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
            <Text size="xs" c="dimmed">
              {oneAtATime}{' '}
              {hangfireDashboardUrl ? (
                <Anchor href={hangfireDashboardUrl} target="_blank" rel="noopener noreferrer" size="xs">
                  Hangfire dashboard <IconExternalLink size={11} />
                </Anchor>
              ) : (
                'The Hangfire dashboard address is not configured for this build.'
              )}
            </Text>
          </Group>
        )}

        {error && (
          <Alert color="red" variant="light" title="Could not run">
            {/* Inline as well as a toast: this says which parameter was wrong, and it should stay on
                screen next to the field rather than vanishing after a few seconds. */}
            {error.message}
          </Alert>
        )}

        {request && (
          <Alert
            color={
              request.status === 'Enqueued'
                ? 'blue'
                : request.status === 'Requested'
                  ? followTimedOut
                    ? 'yellow'
                    : 'blue'
                  : 'red'
            }
            variant="light"
          >
            <Group gap="xs" wrap="nowrap" align="flex-start">
              <Badge size="sm">{request.status}</Badge>
              <Text size="sm">
                {request.status === 'Requested' &&
                  (followTimedOut
                    ? 'Automaton has not claimed this request in a minute; it may be down. The request stays '
                      + 'queued and will run when it returns.'
                    : 'Recorded. Waiting for Automaton to pick it up…')}
                {request.status === 'Enqueued' && (
                  <>
                    Queued as Hangfire job {request.hangfireJobId ?? '(id not recorded)'}. It has not
                    necessarily started.{' '}
                    {jobLink ? (
                      <Anchor href={jobLink} target="_blank" rel="noopener noreferrer">
                        Open in Hangfire <IconExternalLink size={12} />
                      </Anchor>
                    ) : (
                      'The Hangfire dashboard address is not configured for this build.'
                    )}
                  </>
                )}
                {(request.status === 'Rejected' || request.status === 'Failed') &&
                  (requestStatusDetailText(request) ?? 'The run did not start.')}
              </Text>
            </Group>
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            Close
          </Button>
          <Tooltip label={blockedReason} disabled={!blockedReason} multiline w={300} withArrow>
            <Button
              leftSection={blockedReason ? <IconLock size={16} /> : <IconPlayerPlay size={16} />}
              loading={isPending}
              disabled={!!blockedReason}
              data-disabled={!!blockedReason || undefined}
              onClick={() => trigger({ job, parameters: values })}
            >
              Run now
            </Button>
          </Tooltip>
        </Group>
      </Stack>
    </Modal>
  )
}

/** The mark beside a field whose value a link set: that it came from the link, and what it replaced. */
function FromLinkNote({ defaultValue }: { defaultValue: string }) {
  return (
    <Group gap={6}>
      <Badge size="xs" color="orange" variant="light">
        from the link
      </Badge>
      <Text size="xs" c="dimmed">
        default: {defaultValue === '' ? 'empty' : defaultValue}
      </Text>
    </Group>
  )
}

/** How long the window is, always — and loudly when it is longer than the job's own default. */
function WindowLength({ readout }: { readout: WindowReadout }) {
  const { days, defaultDays, longerThanDefault } = readout

  if (days === null) {
    return (
      <Text size="sm" c="dimmed">
        Window length: not known — both bounds must be plain yyyy-mm-dd dates.
      </Text>
    )
  }

  if (days <= 0) {
    return (
      <Text size="sm" c="red">
        The window is empty: the end is exclusive, so it must be after the start. The server will refuse it.
      </Text>
    )
  }

  // longerThanDefault implies a known default.
  return longerThanDefault ? (
    <Alert icon={<IconAlertTriangle size={16} />} color="orange" variant="light" title={`Window: ${daysText(days)}`}>
      <Text size="sm">
        Longer than this job&apos;s default window of {daysText(defaultDays ?? 0)}. A long window is a long
        load — make sure it is the one you mean.
      </Text>
    </Alert>
  ) : (
    <Text size="sm">
      Window: <strong>{daysText(days)}</strong>
      <Text span size="sm" c="dimmed">
        {defaultDays === null ? '' : ` (this job's default is ${daysText(defaultDays)})`}
      </Text>
    </Text>
  )
}

function ParameterField({
  parameter,
  value,
  onChange,
}: {
  parameter: EtlJobParameter
  value: string
  onChange: (value: string) => void
}) {
  const common = {
    label: parameter.label,
    description: parameter.help ?? undefined,
    required: parameter.required,
  }

  if (parameter.kind === 'Enum') {
    return (
      <Select
        {...common}
        data={parameter.options ?? []}
        value={value || null}
        onChange={(v) => onChange(v ?? '')}
        clearable={!parameter.required}
        placeholder={parameter.required ? 'Select one' : 'All'}
      />
    )
  }

  if (parameter.kind === 'Bool') {
    return (
      <Switch
        label={parameter.label}
        description={parameter.help ?? undefined}
        checked={value === 'true'}
        onChange={(e) => onChange(e.currentTarget.checked ? 'true' : 'false')}
      />
    )
  }

  // Dates are plain yyyy-MM-dd text, deliberately. A date picker that hands back an instant is how a
  // timezone gets into a filter against a column that has none -- the API rejects those outright.
  return (
    <TextInput
      {...common}
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
      placeholder={parameter.kind === 'Date' ? 'yyyy-mm-dd' : undefined}
      inputMode={parameter.kind === 'Int' ? 'numeric' : undefined}
    />
  )
}
